import { useContext, useMemo } from 'react';
import { GlobalMenu } from '@components/index';
import { useMutation, useQueryClient } from 'react-query';
import { patchAccessory } from '@services/queries/onboarding/patchAccessory';
import { useParams } from 'react-router-dom';
import { NavigationContext } from '@layout/navigation/navigation';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import {
  assetActionsMenu,
  openChat as mixpanelOpenChat,
} from '@services/mixpanel/trackers/Onboarding';
import { OnboardingFileContext } from '../OnboardingFile/OnboardingFile.query';

interface IQuickActionMenu {
  group: IOnboardingGroup;
  status: string;
}

export const QuickActionMenu = ({ group, status }: IQuickActionMenu) => {
  const queryClient = useQueryClient();

  const params = useParams<{ id: string }>();

  const { studioId, studioName, isSuperAdmin, userEmail, userName } =
    useContext(NavigationContext);
  const { openChat, selectedGroup } = useContext(OnboardingFileContext);

  const accessoryMutation = useMutation(patchAccessory, {
    onSuccess: (_, { status: newStatus }) => {
      queryClient.invalidateQueries(['identifiedonboarding', 'files']);
      queryClient.invalidateQueries(['onboarding', 'files']);
      assetActionsMenu(studioName, params.id, group.id, newStatus);
    },
  });

  const warningDetails = useMemo(() => {
    const errors = [group.mfrError, group.mpnError, group.productNameError];
    const filteredErrors = errors.filter(item => !!item);
    const filteredErrorsLength = filteredErrors.length;
    const isAccessory = !group.isNotAccessory && group.accessoryError;

    if (
      filteredErrorsLength > 0 &&
      ['unidentified', 'identification_requested'].includes(status) &&
      !isAccessory
    ) {
      return {
        warning: `${filteredErrorsLength} ${
          filteredErrorsLength === 1 ? 'Error' : 'Error(s)'
        } Found`,
        warningMessage: `This product has ${filteredErrorsLength} ${
          filteredErrorsLength === 1 ? 'error' : 'errors'
        }, without fixing them you can only add this item as Internal.`,
      };
    }

    return {
      warning: undefined,
      warningMessage: undefined,
    };
  }, [group]);

  const itemsList = useMemo(() => {
    const chatItem = {
      iconName: 'comment',
      label: 'See details',
      onClick: () => {
        openChat.set(true);
        selectedGroup.set(group);
        mixpanelOpenChat(studioName, params.id, group.id);
      },
    };
    const identifyItem = {
      iconName: 'geosearch',
      label: 'Identify Product',
      description: 'May cause identification errors',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'identification_requested',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };
    const requestMoreItem = {
      iconName: 'geosearch',
      label: 'Request More Data',
      description: 'Will create an identification request',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'identification_requested',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };
    const requestItem = {
      iconName: 'geosearch',
      label: 'Request Identification',
      description: 'Will create an identification request',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'identification_requested',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };
    const misItem = {
      iconName: 'geosearch',
      label: 'Product Misidentified',
      description: 'Will create an identification request',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'identification_requested',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };
    const inventoryItem = {
      iconName: 'cube',
      label: 'Add as Internal',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'inventory',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };
    const inventoryDescriptionItem = {
      iconName: 'cube',
      label: 'Add as Internal',
      description:
        'This type of product is added immediatelly, but no additional product data can be included by Beam. However, you’ll be able to add your own information.',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'inventory',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };
    const discardItem = {
      iconName: 'trash',
      label: 'Discard Product',
      onClick: () => {
        accessoryMutation.mutate({
          groupId: group.id,
          status: 'discarded',
          studioId,
          fileId: params.id,
          triggeredBy: userName,
          triggeredByEmail: userEmail,
        });
      },
    };

    switch (isSuperAdmin) {
      case false:
        if (status === 'inventory') return [identifyItem];
        if (status === 'identification_requested') return [chatItem];
        if (status === 'unidentified') return [requestItem];
        return [requestMoreItem, misItem];
      default:
        if (status === 'discarded') return [requestItem, inventoryItem];
        if (status === 'inventory') return [identifyItem, discardItem];
        if (status === 'identification_requested')
          return [chatItem, inventoryItem, discardItem];
        if (status === 'unidentified')
          return [requestItem, inventoryDescriptionItem, discardItem];
        return [requestMoreItem, misItem, inventoryItem, discardItem];
    }
  }, [group]);

  return (
    <GlobalMenu
      title="Quick Actions"
      align="end"
      itemList={itemsList}
      {...warningDetails}
    />
  );
};
